import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwise, CaretDown, CaretUp, CreditCard, Sparkle } from '@phosphor-icons/react';
import ContextHelpButton from '../ContextHelpButton';

// AI-CREDITS-CONNECTIONS-PANEL-001: exposes the EXISTING backend AI
// credit system (GET /api/ai/usage, already used by Profile/Sidebar via
// AuthContext's /plan/limits) inside Connections — this component makes
// its own request instead of reading `useAuth().planLimits` so it has its
// own loading/error/retry lifecycle, independent of whatever else on the
// page happens to have refreshed plan limits.
const PROVIDER_LABEL_KEYS = {
  openai: 'project.connections.aiCredits.providerNames.openai',
  gemini: 'project.connections.aiCredits.providerNames.gemini',
};

// CREDIT-TOPUP-STRATEGIST-TRIAL-001: the Profile-only store is intentionally
// translated here instead of adding another set of global navigation keys.
// The same component remains reusable inside Connections with enableStore=false.
const STORE_COPY = {
  it: {
    usageTitle: 'Usage di questo mese',
    providerCalls: 'chiamate',
    promptTokens: 'Token prompt',
    completionTokens: 'Token risposta',
    storeEyebrow: 'Crediti extra',
    storeTitle: 'Ti serve ancora benzina?',
    storeBody: 'Compra crediti una tantum. Non scadono e restano nel wallet finché non li usi.',
    buy: 'Compra',
    buying: 'Apro Stripe…',
    popular: 'Più scelto',
    storeUnavailable: 'L’acquisto di crediti non è disponibile in questo momento.',
    storeError: 'Non riesco a caricare i pacchetti crediti.',
    purchased: 'Pagamento confermato: i crediti sono stati aggiunti al tuo saldo.',
    cancelled: 'Pagamento annullato. Nessun credito è stato addebitato.',
    purchaseError: 'Non riesco ad avviare il pagamento. Riprova tra poco.',
    limitsNote: 'I crediti extra finanziano le operazioni AI ma non cambiano piano: analytics, feed e modelli premium restano legati al tuo abbonamento.',
    costsTitle: 'Quanto consumano le operazioni',
    textCost: 'Testo / AI editoriale',
    designCost: 'Analisi Design DNA',
    baseImageCost: 'Immagine base (Gemini)',
    premiumImageCost: 'Immagine premium (GPT Image)',
    credits: 'crediti',
  },
  en: {
    usageTitle: 'This month usage',
    providerCalls: 'calls',
    promptTokens: 'Prompt tokens',
    completionTokens: 'Response tokens',
    storeEyebrow: 'Extra credits',
    storeTitle: 'Need a little more fuel?',
    storeBody: 'Buy one-off credits. They do not expire and stay in your wallet until you use them.',
    buy: 'Buy',
    buying: 'Opening Stripe…',
    popular: 'Most popular',
    storeUnavailable: 'Credit purchases are unavailable right now.',
    storeError: 'Credit packs could not be loaded.',
    purchased: 'Payment confirmed: your credits have been added.',
    cancelled: 'Payment cancelled. No credits were charged.',
    purchaseError: 'Checkout could not be started. Please try again shortly.',
    limitsNote: 'Extra credits pay for AI operations but do not change your plan: analytics, feeds and premium models still depend on your subscription.',
    costsTitle: 'Credit cost per operation',
    textCost: 'Text / editorial AI',
    designCost: 'Design DNA analysis',
    baseImageCost: 'Base image (Gemini)',
    premiumImageCost: 'Premium image (GPT Image)',
    credits: 'credits',
  },
  es: {
    usageTitle: 'Uso de este mes',
    providerCalls: 'llamadas',
    promptTokens: 'Tokens de prompt',
    completionTokens: 'Tokens de respuesta',
    storeEyebrow: 'Créditos extra',
    storeTitle: '¿Necesitas un poco más de gasolina?',
    storeBody: 'Compra créditos de una sola vez. No caducan y permanecen en tu saldo hasta que los uses.',
    buy: 'Comprar',
    buying: 'Abriendo Stripe…',
    popular: 'Más elegido',
    storeUnavailable: 'La compra de créditos no está disponible ahora mismo.',
    storeError: 'No se pueden cargar los paquetes de créditos.',
    purchased: 'Pago confirmado: los créditos se añadieron a tu saldo.',
    cancelled: 'Pago cancelado. No se cargaron créditos.',
    purchaseError: 'No se puede iniciar el pago. Inténtalo de nuevo en unos minutos.',
    limitsNote: 'Los créditos extra financian operaciones AI pero no cambian tu plan: analytics, feeds y modelos premium siguen ligados a tu suscripción.',
    costsTitle: 'Consumo de créditos por operación',
    textCost: 'Texto / AI editorial',
    designCost: 'Análisis Design DNA',
    baseImageCost: 'Imagen base (Gemini)',
    premiumImageCost: 'Imagen premium (GPT Image)',
    credits: 'créditos',
  },
  fr: {
    usageTitle: 'Usage de ce mois',
    providerCalls: 'appels',
    promptTokens: 'Tokens prompt',
    completionTokens: 'Tokens réponse',
    storeEyebrow: 'Crédits supplémentaires',
    storeTitle: 'Besoin d’un peu plus de carburant ?',
    storeBody: 'Achetez des crédits ponctuels. Ils n’expirent pas et restent dans votre portefeuille jusqu’à leur utilisation.',
    buy: 'Acheter',
    buying: 'Ouverture de Stripe…',
    popular: 'Le plus choisi',
    storeUnavailable: 'L’achat de crédits est indisponible pour le moment.',
    storeError: 'Impossible de charger les packs de crédits.',
    purchased: 'Paiement confirmé : les crédits ont été ajoutés à votre solde.',
    cancelled: 'Paiement annulé. Aucun crédit n’a été facturé.',
    purchaseError: 'Impossible de démarrer le paiement. Réessayez dans un instant.',
    limitsNote: 'Les crédits supplémentaires financent les opérations IA mais ne changent pas votre offre : analytics, feeds et modèles premium dépendent toujours de votre abonnement.',
    costsTitle: 'Coût en crédits par opération',
    textCost: 'Texte / IA éditoriale',
    designCost: 'Analyse Design DNA',
    baseImageCost: 'Image standard (Gemini)',
    premiumImageCost: 'Image premium (GPT Image)',
    credits: 'crédits',
  },
};

function formatPrice(amountCents, currency, language) {
  try {
    return new Intl.NumberFormat(language, { style: 'currency', currency: String(currency || 'eur').toUpperCase() }).format((Number(amountCents) || 0) / 100);
  } catch {
    return `${((Number(amountCents) || 0) / 100).toFixed(2)} €`;
  }
}

function formatUsdEstimate(amount, language) {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat(language || 'it', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4,
  }).format(numeric);
}

export default function AiCreditsSection({ api, enableStore = false }) {
  const { t, i18n } = useTranslation();
  const language = String(i18n?.resolvedLanguage || i18n?.language || 'it').split('-')[0];
  const storeCopy = STORE_COPY[language] || STORE_COPY.it;
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [packs, setPacks] = useState([]);
  const [creditCosts, setCreditCosts] = useState({});
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeError, setStoreError] = useState(false);
  const [purchasingEnabled, setPurchasingEnabled] = useState(true);
  const [purchaseBusy, setPurchaseBusy] = useState('');
  const [purchaseMessage, setPurchaseMessage] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api.get('/ai/usage')
      .then(({ data }) => setUsage(data || {}))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [api]);

  const loadStore = useCallback(() => {
    if (!enableStore) return;
    setStoreLoading(true);
    setStoreError(false);
    api.get('/billing/credit-packs')
      .then(({ data }) => {
        setPacks(Array.isArray(data?.packs) ? data.packs : []);
        setCreditCosts(data?.credit_costs || {});
        setPurchasingEnabled(data?.purchasing_enabled !== false);
      })
      .catch(() => setStoreError(true))
      .finally(() => setStoreLoading(false));
  }, [api, enableStore]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (enableStore) loadStore(); }, [enableStore, loadStore]);

  useEffect(() => {
    if (!enableStore) return;
    const params = new URLSearchParams(window.location.search);
    const result = params.get('credits');
    const sessionId = params.get('credit_session');
    if (!result && !sessionId) return;

    const cleanUrl = () => {
      params.delete('credits');
      params.delete('credit_session');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    };

    if (result === 'cancelled') {
      setPurchaseMessage(storeCopy.cancelled);
      cleanUrl();
      return;
    }
    if (sessionId) {
      api.get(`/billing/credit-packs/confirm/${encodeURIComponent(sessionId)}`)
        .then(({ data }) => {
          setPurchaseMessage(data?.credited ? storeCopy.purchased : storeCopy.purchaseError);
          load();
          loadStore();
        })
        .catch(() => setPurchaseMessage(storeCopy.purchaseError))
        .finally(cleanUrl);
      return;
    }
    if (result === 'purchased') {
      // get_credit_packs performs best-effort reconciliation of any paid
      // pending purchase, so even a browser that lost the session id still
      // self-heals as soon as Profile is opened again.
      setPurchaseMessage(storeCopy.purchased);
      loadStore();
      load();
      cleanUrl();
    }
  }, [api, enableStore, load, loadStore, storeCopy]);

  const startPurchase = async (packId) => {
    setPurchaseBusy(packId);
    setPurchaseMessage('');
    try {
      const { data } = await api.post(`/billing/credit-packs/${encodeURIComponent(packId)}/checkout`);
      if (!data?.url) throw new Error('checkout_url_missing');
      window.location.href = data.url;
    } catch {
      setPurchaseBusy('');
      setPurchaseMessage(storeCopy.purchaseError);
    }
  };

  if (loading) {
    return (
      <div className="pt-2 pb-12" data-testid="ai-credits-section">
        <section className="card">
          <p className="text-sm text-[var(--text-muted)] py-8 text-center" data-testid="ai-credits-loading">
            {t('common.loading')}
          </p>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-2 pb-12" data-testid="ai-credits-section">
        <section className="card">
          <h3 className="font-semibold flex items-center gap-2">
            <Sparkle size={19} weight="fill" className="text-violet-300" />
            {t('project.connections.tabs.aiCredits')}
          </h3>
          <p className="text-sm text-[var(--text-muted)] mt-4" data-testid="ai-credits-error">
            {t('project.connections.aiCredits.loadError')}
          </p>
          <button className="btn-ghost text-xs mt-3 flex items-center gap-1" onClick={load} data-testid="ai-credits-retry">
            <ArrowClockwise size={13} /> {t('project.connections.aiCredits.retry')}
          </button>
        </section>
      </div>
    );
  }

  // Every number below is read AS-IS from the backend response (never
  // recomputed authoritatively here) — only display formatting (the
  // progress-bar percentage) is derived client-side from used/limit,
  // both of which are themselves backend-provided values.
  const limit = usage?.monthly_limit ?? usage?.limit;
  const used = Number(usage?.monthly_used ?? usage?.used) || 0;
  const unlimited = Boolean(usage?.unlimited);
  const remaining = unlimited ? null : Number(usage?.monthly_remaining ?? usage?.remaining ?? 0);
  const hasMonthlyAllowance = !unlimited && Number(limit || 0) > 0;
  // FREE-PLAN-WALLET-PUBLISHING-001A / part C: the real, authoritative
  // "can I still use AI right now" answer is `total_remaining` (monthly
  // + starter + bonus + purchased) — Free's monthly allowance is 0 by
  // design, so relying on the old monthly-only `remaining` here would
  // wrongly show "exhausted" even when starter credits are available.
  const starterRemaining = Number(usage?.starter_remaining) || 0;
  const bonusRemaining = Number(usage?.bonus_remaining) || 0;
  const purchasedRemaining = Number(usage?.purchased_remaining) || 0;
  const hasWalletBreakdown = starterRemaining > 0 || bonusRemaining > 0 || purchasedRemaining > 0;
  const totalRemaining = unlimited ? null : Number(usage?.total_remaining ?? remaining ?? 0);
  const zeroPlan = !unlimited && !hasMonthlyAllowance && !hasWalletBreakdown && totalRemaining <= 0;
  const exhausted = !unlimited && !zeroPlan && totalRemaining <= 0;
  const pct = unlimited || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100));

  // AI-CREDITS-CONNECTIONS-PANEL-001.1: NEVER default a missing/malformed
  // capability field to "automatic"/"openai"/"gemini" — that would be
  // this component inventing provider information the backend didn't
  // actually send, which is exactly the false-claim risk this task fixes.
  // A field that's genuinely absent renders the neutral "unavailable"
  // label instead; only a real (even if unrecognized) backend value is
  // ever shown as a mode/provider name.
  const rawMode = usage?.mode;
  const hasMode = typeof rawMode === 'string' && rawMode.length > 0;
  const modeLabel = !hasMode
    ? t('project.connections.aiCredits.unavailable')
    : rawMode === 'automatic' ? t('project.connections.aiCredits.modeAutomatic') : rawMode;

  const rawPrimaryProvider = usage?.primary_provider;
  const hasPrimaryProvider = typeof rawPrimaryProvider === 'string' && rawPrimaryProvider.length > 0;
  const primaryProviderLabel = !hasPrimaryProvider
    ? t('project.connections.aiCredits.unavailable')
    : (PROVIDER_LABEL_KEYS[rawPrimaryProvider] ? t(PROVIDER_LABEL_KEYS[rawPrimaryProvider]) : rawPrimaryProvider);

  const rawFallbackProvider = usage?.fallback_provider;
  const hasFallbackProvider = typeof rawFallbackProvider === 'string' && rawFallbackProvider.length > 0;
  const fallbackProviderLabel = !hasFallbackProvider
    ? t('project.connections.aiCredits.unavailable')
    : (PROVIDER_LABEL_KEYS[rawFallbackProvider] ? t(PROVIDER_LABEL_KEYS[rawFallbackProvider]) : rawFallbackProvider);

  const providerCounts = Object.entries(usage?.provider_counts || {}).filter(([, value]) => Number(value) > 0);
  const promptTokens = Number(usage?.prompt_tokens) || 0;
  const completionTokens = Number(usage?.completion_tokens) || 0;
  const hasDetailedUsage = providerCounts.length > 0 || promptTokens > 0 || completionTokens > 0;
  // Tenant USD estimates are server-provided, not inferred from credits.
  // Never display the global aggregate: it includes other customers' usage.
  const usdBudgets = Array.isArray(usage?.usd_estimate?.budgets) ? usage.usd_estimate.budgets : [];
  const usdDay = usdBudgets.find((b) => b?.scope === 'user' && /^\d{4}-\d{2}-\d{2}$/.test(String(b.period)));
  const usdMonth = usdBudgets.find((b) => b?.scope === 'user' && /^\d{4}-\d{2}$/.test(String(b.period)));


  return (
    <div className="pt-2 pb-12" data-testid="ai-credits-section">
      <section className="card">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Sparkle size={19} weight="fill" className="text-violet-300" />
            {t('project.connections.tabs.aiCredits')}
          </h3>
          <div className="flex items-center gap-2">
            <ContextHelpButton articleId="ai-credits" label="Guida crediti AI" />
            <button className="btn-ghost !py-2 !px-3" onClick={() => { load(); if (enableStore) loadStore(); }} title={t('common.refresh')} data-testid="ai-credits-refresh">
              <ArrowClockwise size={14} />
            </button>
          </div>
        </div>

        {/* AI mode */}
        <div className="mt-4">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
            {t('project.connections.aiCredits.modeTitle')}
          </p>
          <p className="text-sm font-medium mt-1" data-testid="ai-credits-mode">
            {modeLabel}
          </p>
          <p className="text-xs text-[var(--text-muted)] mt-1 leading-relaxed">
            {t('project.connections.aiCredits.modeDescription')}
          </p>
        </div>

        {/* Providers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4" data-testid="ai-credits-provider-grid">
          <div className="rounded-2xl border p-3 min-w-0" style={{ borderColor: 'var(--border-color)' }}>
            <p className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
              {t('project.connections.aiCredits.primaryProvider')}
            </p>
            <p className="text-sm font-medium mt-1 truncate" data-testid="ai-credits-primary-provider">
              {primaryProviderLabel}
            </p>
          </div>
          <div className="rounded-2xl border p-3 min-w-0" style={{ borderColor: 'var(--border-color)' }}>
            <p className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
              {t('project.connections.aiCredits.fallbackProvider')}
            </p>
            <p className="text-sm font-medium mt-1 truncate" data-testid="ai-credits-fallback-provider">
              {fallbackProviderLabel}
            </p>
          </div>
        </div>

        {/* Credit balance */}
        <div className="mt-5 pt-4 border-t border-[var(--border-color)]">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
            {t('project.connections.aiCredits.monthlyTitle')}
          </p>

          {unlimited ? (
            <p className="text-lg font-bold mt-2" data-testid="ai-credits-unlimited">{t('profile.unlimited')}</p>
          ) : zeroPlan ? (
            <p className="text-sm text-[var(--text-muted)] mt-2" data-testid="ai-credits-zero-plan">
              {t('project.connections.aiCredits.zeroPlanHint')}
            </p>
          ) : (
            <>
              {hasMonthlyAllowance && (
                <>
                  <p className="text-sm mt-2 break-words" data-testid="ai-credits-used-of-limit">
                    {t('project.connections.aiCredits.usedOfLimit', { used, limit })}
                  </p>
                  <div className="w-full h-2.5 rounded-full overflow-hidden mt-2 bg-[rgba(255,255,255,0.08)]">
                    <div
                      className={`h-full rounded-full ${exhausted ? 'bg-rose-500' : 'bg-gradient-to-r from-violet-500 to-pink-500'}`}
                      style={{ width: `${pct}%` }}
                      data-testid="ai-credits-progress-bar"
                    />
                  </div>
                </>
              )}
              <p className="text-xs text-[var(--text-muted)] mt-1.5" data-testid="ai-credits-remaining">
                {t('project.connections.aiCredits.remaining', { remaining: totalRemaining })}
              </p>
              {hasWalletBreakdown && (
                <ul className="mt-2 space-y-1 text-[11px] text-[var(--text-muted)]" data-testid="ai-credits-wallet-breakdown">
                  {starterRemaining > 0 && <li>{t('project.connections.aiCredits.starterRemaining', { count: starterRemaining })}</li>}
                  {bonusRemaining > 0 && <li>{t('project.connections.aiCredits.bonusRemaining', { count: bonusRemaining })}</li>}
                  {purchasedRemaining > 0 && <li>{t('project.connections.aiCredits.purchasedRemaining', { count: purchasedRemaining })}</li>}
                </ul>
              )}
              {exhausted && (
                <div className="mt-3 rounded-xl border border-rose-400/20 bg-rose-400/5 px-3 py-2" data-testid="ai-credits-exhausted">
                  <p className="text-xs font-semibold text-rose-200">{t('project.connections.aiCredits.exhaustedTitle')}</p>
                  <p className="text-[11px] text-rose-200/80 mt-1 leading-relaxed">
                    {t('project.connections.aiCredits.exhaustedHint')}
                  </p>
                </div>
              )}
            </>
          )}

          <p className="text-[11px] text-[var(--text-muted)] mt-3">
            {t('project.connections.aiCredits.renewalInfo')}
          </p>
        </div>

        {(usdDay || usdMonth) && (
          <div className="mt-4 pt-4 border-t border-[var(--border-color)]" data-testid="ai-credits-usd-estimate">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
              {t('project.connections.aiCredits.usdTitle')}
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[usdDay, usdMonth].filter(Boolean).map((budget) => (
                <div key={budget.period} className="rounded-xl border border-[var(--border-color)] p-3">
                  <p className="text-xs text-[var(--text-muted)]">
                    {budget === usdDay ? t('project.connections.aiCredits.usdDay') : t('project.connections.aiCredits.usdMonth')}
                  </p>
                  <p className="text-sm font-semibold mt-1">
                    {formatUsdEstimate(budget.estimated_used_usd, language)} / {formatUsdEstimate(budget.limit_usd, language)}
                  </p>
                  <p className="text-xs mt-1 text-[var(--text-muted)]">
                    {t('project.connections.aiCredits.usdRemaining', { value: formatUsdEstimate(budget.remaining_usd, language) })}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-3">
              {t('project.connections.aiCredits.usdNotice')}
            </p>
          </div>
        )}

        {hasDetailedUsage && (
          <div className="mt-4 pt-4 border-t border-[var(--border-color)]" data-testid="ai-credits-detailed-usage">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">{storeCopy.usageTitle}</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {providerCounts.map(([provider, count]) => (
                <div key={provider} className="rounded-xl border border-[var(--border-color)] p-2.5">
                  <p className="text-[10px] text-[var(--text-muted)] uppercase">{provider}</p>
                  <p className="text-sm font-semibold mt-1">{count} {storeCopy.providerCalls}</p>
                </div>
              ))}
              {promptTokens > 0 && (
                <div className="rounded-xl border border-[var(--border-color)] p-2.5">
                  <p className="text-[10px] text-[var(--text-muted)] uppercase">{storeCopy.promptTokens}</p>
                  <p className="text-sm font-semibold mt-1">{promptTokens.toLocaleString()}</p>
                </div>
              )}
              {completionTokens > 0 && (
                <div className="rounded-xl border border-[var(--border-color)] p-2.5">
                  <p className="text-[10px] text-[var(--text-muted)] uppercase">{storeCopy.completionTokens}</p>
                  <p className="text-sm font-semibold mt-1">{completionTokens.toLocaleString()}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Explanation */}
        <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
          <button
            className="flex items-center justify-between w-full text-left"
            onClick={() => setExplanationOpen((value) => !value)}
            data-testid="ai-credits-explanation-toggle"
          >
            <span className="text-xs font-semibold">{t('project.connections.aiCredits.explanationTitle')}</span>
            {explanationOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </button>
          {explanationOpen && (
            <ul
              className="mt-2 space-y-1.5 text-[11px] text-[var(--text-muted)] list-disc list-inside leading-relaxed"
              data-testid="ai-credits-explanation-body"
            >
              <li>{t('project.connections.aiCredits.explanationItems.textGeneration')}</li>
              <li>{t('project.connections.aiCredits.explanationItems.aiAnalysis')}</li>
              <li>{t('project.connections.aiCredits.explanationItems.imageGeneration')}</li>
              <li>{t('project.connections.aiCredits.explanationItems.designAnalysis')}</li>
              <li>{t('project.connections.aiCredits.explanationItems.other')}</li>
            </ul>
          )}
        </div>

        {enableStore && !unlimited && (
          <div className="mt-5 pt-5 border-t border-[var(--border-color)]" data-testid="ai-credit-store">
            <p className="text-[10px] font-bold uppercase tracking-[.18em] text-fuchsia-300">{storeCopy.storeEyebrow}</p>
            <h4 className="mt-1 text-base font-bold">{storeCopy.storeTitle}</h4>
            <p className="mt-1 text-xs leading-5 text-[var(--text-muted)]">{storeCopy.storeBody}</p>

            {purchaseMessage && (
              <div className="mt-3 rounded-xl border border-[var(--border-color)] bg-white/[.03] px-3 py-2 text-xs" data-testid="ai-credit-store-message">
                {purchaseMessage}
              </div>
            )}

            {storeLoading ? (
              <p className="mt-4 text-xs text-[var(--text-muted)]">{t('common.loading')}</p>
            ) : storeError ? (
              <p className="mt-4 text-xs text-rose-300">{storeCopy.storeError}</p>
            ) : !purchasingEnabled ? (
              <p className="mt-4 text-xs text-[var(--text-muted)]">{storeCopy.storeUnavailable}</p>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {packs.map(pack => (
                  <div
                    key={pack.id}
                    className="relative rounded-2xl border p-4"
                    style={{ borderColor: pack.popular ? 'rgba(168,85,247,.45)' : 'var(--border-color)', background: pack.popular ? 'rgba(168,85,247,.06)' : 'transparent' }}
                    data-testid={`ai-credit-pack-${pack.id}`}
                  >
                    {pack.popular && <span className="absolute -top-2 right-3 rounded-full bg-fuchsia-500 px-2 py-0.5 text-[9px] font-bold text-white">{storeCopy.popular}</span>}
                    <p className="text-2xl font-black">{pack.credits}</p>
                    <p className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{storeCopy.credits}</p>
                    <p className="mt-3 text-sm font-semibold">{formatPrice(pack.amount_cents, pack.currency, language)}</p>
                    <button
                      type="button"
                      className="btn-ghost mt-3 w-full justify-center text-xs"
                      onClick={() => startPurchase(pack.id)}
                      disabled={Boolean(purchaseBusy)}
                    >
                      <CreditCard size={14} /> {purchaseBusy === pack.id ? storeCopy.buying : storeCopy.buy}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {Object.keys(creditCosts).length > 0 && (
              <div className="mt-4 rounded-2xl border border-[var(--border-color)] p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">{storeCopy.costsTitle}</p>
                <div className="mt-2 grid gap-1.5 text-[11px] text-[var(--text-secondary)] sm:grid-cols-2">
                  <span>{storeCopy.textCost}: <strong>{creditCosts.text ?? 1}</strong></span>
                  <span>{storeCopy.designCost}: <strong>{creditCosts.design_analysis ?? 8}</strong></span>
                  <span>{storeCopy.baseImageCost}: <strong>{creditCosts.image_gemini ?? 4}</strong></span>
                  <span>{storeCopy.premiumImageCost}: <strong>{creditCosts.image_openai ?? 10}</strong></span>
                </div>
              </div>
            )}

            <p className="mt-3 text-[11px] leading-5 text-[var(--text-muted)]">{storeCopy.limitsNote}</p>
          </div>
        )}
      </section>
    </div>
  );
}
